<template>
	<div class="search-results main-container">
		<h1>
			{{ $t("search.search_with") }} <span v-if="displayTitle">"{{ displayTitle }}"</span>
		</h1>
		<div v-if="results?.search_results === 0">{{ $t("search.no_results") }}.</div>

		<div class="results" v-focus-section>
			<div class="item" v-focus v-for="item in results" :key="item.uuid">
				<component :is="getTeaserComponent(item)" :item="item" />
			</div>
		</div>
		<div class="pagination" v-if="totalPages > 1">
			<button @click="prevPage" :hidden="currentPage === 0" :disabled="currentPage === 0">
				{{ $t("pager.prev") }}
			</button>
			<span>{{ currentPage + 1 }} of {{ totalPages }}</span>
			<button @click="nextPage" :hidden="currentPage >= totalPages - 1" :disabled="currentPage >= totalPages - 1">
				{{ $t("pager.next") }}
			</button>
		</div>
	</div>
	<TeaserPreview />
</template>

<script lang="ts" setup>
	import { ref, watch, computed } from "vue"
	import { useRoute, useRouter } from "vue-router"
	import { searchContent } from "../composables/useSearch"
	import { TeaserSmall } from "../components/teasers"
	import { useUrlSearchParams } from "@vueuse/core"
	import { useTaxonomyStore } from "../stores/taxonomies"
	import { useTeaserStore } from "../stores/teaser"
	import TeaserPreview from "../components/video/TeaserPreview.vue"
	import { useI18n } from "vue-i18n"

	const { locale } = useI18n()
	const teaserStore = useTeaserStore()
	const router = useRouter()
	const route = useRoute()
	const params = useUrlSearchParams("history")
	const taxonomyStore = useTaxonomyStore()
	const results = ref<any[]>([])
	const currentPage = ref(0)
	const totalPages = ref(0)
	const pageSize = ref(18) // default from API
	const totalResults = ref(0)

	const getTeaserComponent = (item: any) => TeaserSmall

	const searchQuery = computed(() => (route.query.q as string) || "")

	const categoryTitle = computed(() => {
		const filter = params["f[0]"]
		if (filter && typeof filter === "string" && filter.includes("category_id:")) {
			const id = filter.split(":")[1]
			return taxonomyStore.getCategoryTitle(Number(id)) || ""
		}
		return ""
	})

	const displayTitle = computed(() => {
		if (searchQuery.value && categoryTitle.value) {
			return `${searchQuery.value} (${categoryTitle.value})`
		}
		return searchQuery.value || categoryTitle.value || ""
	})

	// --- FETCH RESULTS FUNCTION --- //
	const fetchResults = async (page = 0) => {
		const index = route.query.index as string
		const filters = params["f[0]"] ? [params["f[0]"] as string] : [""]

		const response = await searchContent(searchQuery.value, index, filters, locale.value, page)

		results.value = response.search_results.filter((item) => item.status === "1") // optional filter
		currentPage.value = response.search_results_page
		totalPages.value = response.search_results_pages
		pageSize.value = response.search_results_per_page
		totalResults.value = response.search_results_count
	}

	// --- WATCH FOR QUERY CHANGES --- //
	watch(
		() => [route.query.q, route.query.index],
		async () => {
			currentPage.value = 0
			await fetchResults(0)
		},
		{ immediate: true }
	)

	// --- PAGINATION --- //
	const nextPage = async () => {
		if (currentPage.value < totalPages.value - 1) {
			await fetchResults(currentPage.value + 1)
		}
	}

	const prevPage = async () => {
		if (currentPage.value > 0) {
			await fetchResults(currentPage.value - 1)
		}
	}
</script>

<style lang="scss" scoped>
	h1 {
		margin-bottom: 35px;

		@include br.respond-to(br.$tablet-large) {
			font-size: 26px;
			line-height: 38px;
			margin-bottom: 25px;
		}
	}
	.search-results {
		padding: 80px 0;

		@include br.tablet-down {
			padding: 120px 0;
		}
	}
	.results {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
		column-gap: 20px;
		row-gap: 45px;

		@include br.tablet-down {
			grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
			column-gap: 15px;
			row-gap: 20px;
		}
	}
	.pagination {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 12px;
		margin-top: 30px;
		button {
			padding: 10px 20px;
			border: 2px solid var(--red);
			color: white;
			background: var(--red);
			border-radius: 10px;
			transition: all 0.3s ease-in-out;
			font-size: 16px;
			font-weight: 600;
			&:hover {
				cursor: pointer;
				background: var(--white);
				color: var(--red);
			}
			@include br.respond-to(br.$mobile-xlarge) {
				padding: 5px 15px;
				font-size: 14px;
			}
		}
	}
</style>
